import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import validator from 'validator';
import { login, loginVerifyCode } from "../../../services/AuthService";
import Utils from "../../../utils";
import { CLIENT_LOCAL_STORE, SALT } from "../../../configs/AppConfig";
import { encryptData } from "../../../utils/crypto";
import { Lang } from "../../../lang";
import { useGoogleLogin } from '@react-oauth/google';
import Logo from '../../../assets/Images/Docusafe_logo.webp';
import './AuthPages.css';

function Login() {
  document.title = Lang.login_title;
  document.getElementsByTagName("META")[4].content = Lang.login_meta_desc;
  document.getElementsByTagName("META")[5].content = Lang.login_meta_keyword;

  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isVerify, setIsVerify] = useState(false);
  const [userData, setUserData] = useState({});
  const [code, setCode] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);

  let errorsObj = { email: '', password: '', code: '' };
  const [errors, setErrors] = useState(errorsObj);

  function onLogin(e) {
    e.preventDefault();
    let error = false;
    const errorObj = { ...errorsObj };
    if (email === '') {
      errorObj.email = 'Email is required';
      error = true;
    } else if (!validator.isEmail(email)) {
      errorObj.email = 'Please enter valid email address';
      error = true;
    }
    if (password === '') {
      errorObj.password = 'Password is required';
      error = true;
    }
    setErrors(errorObj);
    if (error) return;
    setLoading(true);
    let obj = {
      email: email,
      password: password
    };
    login(obj)
      .then(response => {
        if (response.data.data.qr_code) {
          setIsVerify(true);
          setLoading(false);
          setUserData(response.data.data);
        } else {
          let encryptedData = encryptData(response.data.data, SALT);
          localStorage.setItem(CLIENT_LOCAL_STORE, encryptedData);
          Utils.updateLastActivity();
          navigate('/client-portal');
          setLoading(false);
        }
      })
      .catch(err => {
        toast.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  }

  const onVerify = (e) => {
    e.preventDefault();
    let error = false;
    const errorObj = { ...errorsObj };
    if (!code) {
      errorObj.code = 'Code is required';
      error = true;
    }
    setErrors(errorObj);
    if (error) return;
    setLoading(true);
    let data = { ...userData };
    let obj = {
      id: (data.id),
      company_login_id: (data.company_user_id),
      code: code
    };
    loginVerifyCode(obj)
      .then(response => {
        let encryptedData = encryptData(userData, SALT);
        localStorage.setItem(CLIENT_LOCAL_STORE, encryptedData);
        Utils.updateLastActivity();
        setLoading(false);
        navigate('/client-portal');
      })
      .catch(err => {
        toast.error(Utils.getErrorMessage(err));
        setLoading(false);
      });
  };

  return (
    <div className="login-container p-0 ">
      {/* Background Elements */}
      <div className="bg-elements">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
      </div>


      <div className="login-content">
        {/* Left Side - Branding */}
        <div className="login-branding">
          <div className="brand-content">
            <div className="brand-text">
              <h2>Welcome to<br />Visa Process Management</h2>
              <p className="my-3">Streamline your visa applications with <br />our secure, AI-powered document management system<br /> designed for modern immigration services.
              </p>
            </div>
            <div className="feature-highlights">
              <div className="feature-item">
                <div className="icon-box">
                  <i className="bi bi-file-lock"></i>
                </div>
                <span>GDPR Compliance</span>
              </div>

              <div className="feature-item">
                <div className="icon-box">
                  <i className="bi bi-people"></i>
                </div>
                <span>Role-based access</span>
              </div>

              <div className="feature-item">
                <div className="icon-box">
                  <i className="bi bi-lock"></i>
                </div>
                <span>Progress tracking</span>
              </div>

              <div className="feature-item">
                <div className="icon-box">
                  <i className="bi bi-file-earmark-text"></i>
                </div>
                <span>Digital Signatures</span>
              </div>
            </div>

          </div>
        </div>

  
        <div className="login-form-section">
          <div className="auth-card">
            <div className="card-header-auth">
              <img src={Logo} alt="Docusafe Logo" className="docusafe_logo" />
              <h2 >Welcome Back</h2>
              <p>Access your secure visa management portal</p>
            </div>

            {isVerify ? (
              <form onSubmit={onVerify} className="auth-form">
                <div className="form-group">
                  <label className="form-label">
                    <i className="bi bi-shield-lock"></i>
                    Authenticator Code
                  </label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      className={`form-control ${errors.code ? 'error' : ''}`}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Enter 6-digit code"
                      maxLength="6"
                    />
                  </div>
                  {errors.code && <div className="error-message-auth">{errors.code}</div>}
                </div>

                <button type="submit" className="submit-btn" disabled={loading}>
                  <span>Verify & Continue</span>
                  <i className="bi bi-arrow-right"></i>
                </button>

                <button
                  type="button"
                  className="back-btn"
                  onClick={() => setIsVerify(false)}
                >
                  <i className="bi bi-arrow-left"></i>
                  Back to Login
                </button>
              </form>
            ) : (
              <form onSubmit={onLogin} className="auth-form">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    {/* <i className="bi bi-envelope"></i> */}
                    Email Address
                  </label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      className={`form-control ${errors.email ? 'error' : ''}`}
                      id="email"
                      value={email}
                      maxLength="50"
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                    />
                  </div>
                  {errors.email && <div className="error-message-auth">{errors.email}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    {/* <i className="bi bi-lock"></i> */}
                    Password
                  </label>
                  <div className="input-wrapper password-wrapper">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      className={`form-control ${errors.password ? 'error' : ''}`}
                      value={password}
                      maxLength="15"
                      onChange={(e) => setPassword(e.target.value)}
                      id="password"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                    >
                      <i className={`bi ${passwordVisible ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                  {errors.password && <div className="error-message-auth">{errors.password}</div>}
                </div>

                <button type="submit" className="submit-btn" disabled={loading}>
                  <span>Sign In to Portal</span>
                  <i className="bi bi-arrow-right"></i>
                </button>

                <p className="signup-text">
                  Don't have an account? <NavLink to="/sign-up" className="signup-link text-decoration-none">Register</NavLink>
                </p>
              </form>
            )}


          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
